import { Component, EventEmitter, Input, Output, OnInit, input, computed, effect, OnChanges } from '@angular/core';
import FlagTypes = Boo.Objects.Work.SeoMinute.Enums.FlagTypes;
import SeoWorkTaskType = Boo.Objects.Work.SeoMinute.SeoWorkTaskType;
import { Periods } from '../../Models/periods';
import { WorkPeriod, WorkQtyUpdated, WorkAdded } from '../../Models/models';
import { NonworkableTaskTypeService } from '../../../../../services/nonworkable-tasktype.service';
import { NextAvailableWorkAdded } from '../../Models/next-available-work-added';
import { DEFAULT_MAX_TASK_COUNT } from '../../../../../shared/constants';

const DEFAULT_QTY = 1;
const DEFAULT_NEXT_AVAILABLE_PERIOD = -1;

@Component({
  selector: 'app-components-managecustomer-allocatework-task-type-period-selection',
  templateUrl: './task-type-period-selection.component.html',
  styleUrls: ['./task-type-period-selection.component.scss']
})
export class TaskTypePeriodSelectionComponent implements OnInit, OnChanges {
  taskTypes = input<SeoWorkTaskType[]>([]);
  @Input() customerId: number;
  @Input() periods: WorkPeriod[];
  @Input() user: Boo.Objects.User;
  @Input() noAvailablePeriodsErrorMessage: string;
  @Output() addTasksOfType: EventEmitter<WorkQtyUpdated> = new EventEmitter<WorkQtyUpdated>();
  @Output() addCustomTaskType: EventEmitter<WorkAdded> = new EventEmitter<WorkAdded>();
  @Output() addNextAvailableTaskType: EventEmitter<NextAvailableWorkAdded> = new EventEmitter<NextAvailableWorkAdded>();
  @Output() refresh: EventEmitter<any> = new EventEmitter<any>();
  @Output() taskTypeChanged = new EventEmitter<void>();

  displayPeriods: WorkPeriod[] = [];
  selectedPeriod: number = Periods.current;
  selectedTaskType: SeoWorkTaskType;
  orderableTaskTypes = computed<SeoWorkTaskType[]>(() => this.taskTypes().filter(x =>
    !x.Flags.some(f => f.Type === FlagTypes.NotVisibleForOrdering)), { equal: () => false});

  qtyNumbers: number[] = [];
  selectedQty = DEFAULT_QTY;
  nextAvailablePeriod = DEFAULT_NEXT_AVAILABLE_PERIOD;

  get isCurrentPeriod(): boolean {
    return this.selectedPeriod === Periods.current;
  }

  constructor(private nonworkableTaskTypeService: NonworkableTaskTypeService) {
    effect(() => {
      this.selectedTaskType = this.orderableTaskTypes().find(x => x.TaskTypeId === this.selectedTaskType?.TaskTypeId);
    });
  }

  ngOnInit(): void {
    this.qtyNumbers = Array.from({ length: DEFAULT_MAX_TASK_COUNT }, (_, i) => i + 1);
  }

  ngOnChanges(): void {
    if (this.periods?.length) {
      this.displayPeriods = [
        {
          period: DEFAULT_NEXT_AVAILABLE_PERIOD,
          displayName: 'Next Available',
          taskTypeGroups: null,
          minutes: 0,
          errors: []
        },
        ...this.periods
      ];
      this.selectedPeriod = this.displayPeriods[0].period;
    } else {
      this.displayPeriods = [];
    }
  }

  add(quantity?: number): void {
    quantity = quantity ? quantity : DEFAULT_QTY;

    if (this.selectedPeriod == DEFAULT_NEXT_AVAILABLE_PERIOD) {
      this.addNextAvailableTaskType.emit({ type: this.selectedTaskType });
    }
    else if (this.selectedTaskType.IsCustom) {
      this.addCustomTaskType.emit({ type: this.selectedTaskType, period: this.selectedPeriod });
    }
    else {
      this.addTasksOfType.emit({ type: this.selectedTaskType, quantity: quantity, period: this.selectedPeriod });
    }
  }

  clearSelectedTaskType(): void {
    this.selectedTaskType = undefined;
  }

  onTaskTypeChanged(): void {
    this.taskTypeChanged.emit();
  }

  deactivate(taskType: SeoWorkTaskType): void {
    this.nonworkableTaskTypeService.deactivate(this.customerId, taskType.TaskTypeId, this.user.UserId)
      .subscribe({
        next: () => {
          toastr.success(`${this.selectedTaskType.Name} has been deactivated`);
          this.selectedTaskType = undefined;
          this.refresh.emit();
        },
        error: (err) => toastr.error(err)
      });
  }

  canBeOrdered(taskType: SeoWorkTaskType): boolean {
    return !taskType.Flags?.some(x => x.Type === FlagTypes.Nonworkable
      || x.Type === FlagTypes.Unavailable
      || (x.Type === FlagTypes.ExaustedForPeriod && this.isCurrentPeriod));
  }

  canBeNonworkable(taskType: SeoWorkTaskType): boolean {
    return !this.isNonworkable(taskType)
      && !taskType.IsCustom
      && ![Boo.Objects.Enums.TaskTypeEnum.ProfessionalBacklink,
        Boo.Objects.Enums.TaskTypeEnum.ProfessionalInternationalBacklink,
        Boo.Objects.Enums.TaskTypeEnum.LocalBusinessCitationSubscription
      ].includes(taskType.TaskTypeEnum);
  }

  isNonworkable(taskType: SeoWorkTaskType): boolean {
    return taskType.Flags?.some(x => x.Type === FlagTypes.Nonworkable
      || (x.Type === FlagTypes.ExaustedForPeriod && this.isCurrentPeriod));
  }

  isTemporarilyNonworkable(taskType: SeoWorkTaskType): boolean {
    return taskType.Flags?.some(x => x.Type === FlagTypes.ExaustedForPeriod && this.isCurrentPeriod) && !taskType.Flags?.some(x => x.Type === FlagTypes.Nonworkable);
  }
 
  isNonworkableFlag(flag: Boo.Objects.Work.SeoMinute.TaskTypeFlag): boolean {
    return flag.Type === FlagTypes.Nonworkable || (flag.Type === FlagTypes.ExaustedForPeriod && this.isCurrentPeriod);
  }

  showDeactivateButton(taskType: SeoWorkTaskType): boolean {
    return (this.canBeNonworkable(taskType) || this.isTemporarilyNonworkable(taskType)) && !this.canBeOrdered(taskType);
  }

  getTitle(taskType: SeoWorkTaskType): string {
    return !this.canBeOrdered(taskType) ? this.getFlagMessages(taskType, [FlagTypes.Nonworkable, FlagTypes.Unavailable]) :
      this.hasWarnings(taskType) ? this.getFlagMessages(taskType, [FlagTypes.Warning]) :
      'Available';
  }

  getIconClass(taskType: SeoWorkTaskType): string {
    return this.isNonworkable(taskType) ? 'fas fa-stop-circle text-warning' :
      !this.canBeOrdered(taskType) ? 'fas fa-stop-circle text-danger' :
      this.hasWarnings(taskType) ? 'fas fa-exclamation-circle text-warning' :
      'fas fa-check-square text-success';
  }

  searchTaskTypes(term: string, item: SeoWorkTaskType): boolean {
    term = term.toLowerCase();
    return item.Name.toLowerCase().indexOf(term) > -1 || item.Minutes.toString().indexOf(term) > -1;
  }

  qtyDisabled(qty): boolean {
    const taskTypeGroup = this.periods?.find(x => x.period === this.selectedPeriod)
      ?.taskTypeGroups?.find(x => x.taskTypeId === this.selectedTaskType?.TaskTypeId);
    return qty > (this.selectedTaskType?.MaxCount ?? DEFAULT_MAX_TASK_COUNT) - (taskTypeGroup?.workItems?.length ?? 0);
  }

  private hasWarnings(taskType: SeoWorkTaskType): boolean {
    return taskType.Flags.some(x => x.Type === FlagTypes.Warning);
  }

  private getFlagMessages(taskType: SeoWorkTaskType, flagTypes: FlagTypes[]): string {
    return taskType
      .Flags
      .filter(x => flagTypes.includes(x.Type))
      .map(x => x.Message)
      .join(', ');
  }
}
